import React, { createContext, useState } from "react";
import { IPlan, ISubscription } from '@leedpulse-monorepo/interfaces';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import getPlans from "../../../domain/use_cases/plans/getPlans";
import { EProductType, ContextStatus } from "@leedpulse-monorepo/enums";
import searchPlans from "../../../domain/use_cases/plans/searchPlans";
import getPlanById from "../../../domain/use_cases/plans/getPlanById";
import getPlansRefs, { ISmallPlan } from "../../../domain/use_cases/plans/getPlansRefs";

export interface IExtendedPlan extends IPlan {
  subscriptions?: ISubscription[];
  subscription_count?: number;
}

interface PlanOptions {
  product: EProductType;
}

const PlansContext = createContext({
  count: 0,
  leedpulseCount: 0,
  leedflowCount: 0,
  items: [] as IPlan[],
  selected: null as IExtendedPlan | null,
  lastVisible: {} as QueryDocumentSnapshot<DocumentData, DocumentData>,
  searchResults: [] as IPlan[],
  noSearchResults: false,
  fetch: (options?: PlanOptions, pagination?: boolean) => new Promise((resolve, reject) => null),
  fetchNext: (options?: PlanOptions) => new Promise((resolve, reject) => null),
  fetchAndSelectById: (_: string) => new Promise((resolve, reject) => null),
  fetchPlansRefs: () => new Promise((resolve, reject) => null),
  search: (_: string, __: PlanOptions) => {return;},
  resetSearch: () => {return;},
  reset: () => {return;},
  status: ContextStatus.IDLE,
  selectionStatus: ContextStatus.IDLE,
  plansRefs: [] as ISmallPlan[],
});

export const PLANS_FETCH_LIMIT = 10;

export const PlansProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [count, setCount] = useState(0);
  const [leedpulseCount, setLeedpulseCount] = useState(0);
  const [leedflowCount, setLeedflowCount] = useState(0);
  const [items, setItems] = useState([] as IPlan[]);
  const [selected, setSelected] = useState(null as IExtendedPlan | null);
  const [lastVisible, setLastVisible] = useState(
    {} as QueryDocumentSnapshot<DocumentData, DocumentData>
  );
  const [searchResults, setSearchResults] = useState([] as IPlan[]);
  const [noSearchResults, setNoSearchResults] = useState(false);
  const [selectionStatus, setSelectionStatus] = useState(ContextStatus.IDLE);
  const [status, setStatus] = useState(ContextStatus.IDLE);
  const [plansRefs, setPlansRefs] = useState<ISmallPlan[]>([]);

  const fetch = async (options?: PlanOptions, pagination = false) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getPlans(pagination ? PLANS_FETCH_LIMIT : undefined, undefined, options);
      setItems(useCaseResponse.plans);
      setLastVisible(useCaseResponse.lastVisible);
      setCount(useCaseResponse.totalCollectionCount);
      if (useCaseResponse.productCounts) {
        setLeedpulseCount(useCaseResponse.productCounts[EProductType.LEEDPULSE]);
        setLeedflowCount(useCaseResponse.productCounts[EProductType.LEEDFLOW]);
      }
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchNext = async (options?: PlanOptions) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getPlans(
        PLANS_FETCH_LIMIT,
        lastVisible,
        options
      );
      setItems([...items, ...useCaseResponse.plans]);
      setLastVisible(useCaseResponse.lastVisible);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchAndSelectById = async (id: string) => {
    setSelectionStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getPlanById(id);
      if (useCaseResponse) {
        setSelected(useCaseResponse);
        setSelectionStatus(ContextStatus.SUCCESS);
      } else {
        setSelectionStatus(ContextStatus.ERROR);
      }
    } catch (e) {
      setSelectionStatus(ContextStatus.ERROR);
    }
  };

  const fetchPlansRefs = async () => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getPlansRefs({product: selected?.product});
      setPlansRefs(useCaseResponse.plansRefs);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const search = async (searchQuery: string, options: PlanOptions) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await searchPlans(searchQuery, options.product);
      setSearchResults(useCaseResponse.plans);
      setNoSearchResults(useCaseResponse.plans.length === 0);
      setCount(useCaseResponse.totalCollectionCount);
      if (useCaseResponse.productCounts) {
        setLeedpulseCount(useCaseResponse.productCounts[EProductType.LEEDPULSE]);
        setLeedflowCount(useCaseResponse.productCounts[EProductType.LEEDFLOW]);
      }
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const resetSearch = () => {
    setSearchResults([]);
    setNoSearchResults(false);
  };

  const reset = () => {
    setItems([]);
    setCount(0);
    setLastVisible({} as QueryDocumentSnapshot<DocumentData, DocumentData>);
    setStatus(ContextStatus.IDLE);
  };

  return (
    <PlansContext.Provider
      value={{
        count,
        leedpulseCount,
        leedflowCount,
        items,
        selected,
        lastVisible,
        searchResults,
        noSearchResults,
        fetch,
        fetchNext,
        fetchAndSelectById,
        fetchPlansRefs,
        search,
        resetSearch,
        reset,
        status,
        selectionStatus,
        plansRefs,
      }}
    >
      {children}
    </PlansContext.Provider>
  );
};

export default PlansContext;
